import { Controller } from "@hotwired/stimulus";
import { contributionForGap, currentTopUpAmount, applyAddOnToScenarioTotals } from "../scenario_modeling/contribution";
import {
  GTU_PRECISION,
  formatNumber,
  hideModelingLoading,
  showModelingLoading,
} from "../scenario_modeling/payload_refresh";
import { volBoughtValidationStatus } from "../scenario_modeling/vol_bought_validation";

const VOL_BOUGHT_CELL_CLASSES = {
  match: [ "bg-success-50" ],
  close: [ "bg-warning-50" ],
  mismatch: [ "bg-red-50" ],
  unknown: [ "bg-warning-50" ],
  neutral: [ "bg-warning-50" ],
};
const ALL_VOL_BOUGHT_CELL_CLASSES = [ ...new Set(Object.values(VOL_BOUGHT_CELL_CLASSES).flat()) ];

export default class extends Controller {
  static targets = [
    "totalContribution",
    "gapClosureLabel",
    "contributionBreakdown",
    "grossContribution",
    "addedCosts",
    "contributionCell",
    "meanCell",
    "gtuCell",
    "countryContribution",
    "countryVolDeclared",
    "countryBuyingPct",
    "countryGtu",
    "salaryMatrices",
    "workersReceiving",
    "volDeclared",
    "volBoughtCell",
  ];

  static values = {
    baseGap: Number,
    gapTarget: { type: Number, default: 100 },
    gapClosureTemplate: String,
    currencyDisplay: { type: String, default: "payment" },
    panelId: String,
    paymentCurrencyCode: String,
    taxesLevies: { type: Number, default: 0 },
    otherCosts: { type: Number, default: 0 },
    includeAddOn: { type: Boolean, default: false },
  };

  connect() {
    this._onGapTargetChange = this.updateGapTarget.bind(this);
    this._onCurrencyDisplayChange = this.updateCurrencyDisplay.bind(this);
    this._onExchangeRatesLoading = this.showLoading.bind(this);
    this._onExchangeRatesReady = this.hideLoading.bind(this);
    this._onPayloadApplied = this.syncFromPayload.bind(this);
    this._onExchangeRatesLoaded = this.syncFromPayload.bind(this);
    this._onExchangeRatesError = this.restoreAfterError.bind(this);
    this._onCostsChange = this.updateCosts.bind(this);
    this._onVolDeclaredChange = this.updateVolDeclared.bind(this);
    // A table page swapped in by Turbo is rendered from the saved wizard, which
    // can lag behind the gap target and currency shown on the page.
    this._onFrameLoad = () => this._renderFacilityRows();
    this._onTopUpChange = () => {
      if (this.includeAddOnValue) applyAddOnToScenarioTotals();
    };

    window.addEventListener("scenario-modeling:gap-target-change", this._onGapTargetChange);
    window.addEventListener("scenario-modeling:currency-display-change", this._onCurrencyDisplayChange);
    window.addEventListener("scenario-modeling:exchange-rates-loading", this._onExchangeRatesLoading);
    window.addEventListener("scenario-modeling:exchange-rates-ready", this._onExchangeRatesReady);
    window.addEventListener("scenario-modeling:payload-applied", this._onPayloadApplied);
    window.addEventListener("scenario-modeling:exchange-rates-loaded", this._onExchangeRatesLoaded);
    window.addEventListener("scenario-modeling:exchange-rates-error", this._onExchangeRatesError);
    window.addEventListener("scenario-modeling:costs-change", this._onCostsChange);
    window.addEventListener("scenario-modeling:vol-declared-change", this._onVolDeclaredChange);
    window.addEventListener("scenario-modeling:top-up-change", this._onTopUpChange);
    this.element.addEventListener("turbo:frame-load", this._onFrameLoad);

    this.currencyDisplayValue = this._normalizeCurrencyDisplay(
      document.getElementById("scenario_currency_display")?.value
    );
  }

  disconnect() {
    window.removeEventListener("scenario-modeling:gap-target-change", this._onGapTargetChange);
    window.removeEventListener("scenario-modeling:currency-display-change", this._onCurrencyDisplayChange);
    window.removeEventListener("scenario-modeling:exchange-rates-loading", this._onExchangeRatesLoading);
    window.removeEventListener("scenario-modeling:exchange-rates-ready", this._onExchangeRatesReady);
    window.removeEventListener("scenario-modeling:payload-applied", this._onPayloadApplied);
    window.removeEventListener("scenario-modeling:exchange-rates-loaded", this._onExchangeRatesLoaded);
    window.removeEventListener("scenario-modeling:exchange-rates-error", this._onExchangeRatesError);
    window.removeEventListener("scenario-modeling:costs-change", this._onCostsChange);
    window.removeEventListener("scenario-modeling:vol-declared-change", this._onVolDeclaredChange);
    window.removeEventListener("scenario-modeling:top-up-change", this._onTopUpChange);
    this.element.removeEventListener("turbo:frame-load", this._onFrameLoad);
  }

  updateCosts(event) {
    if (this.hasPanelIdValue && event.detail?.panelId && event.detail.panelId !== this.panelIdValue) {
      return;
    }

    if (event.detail?.taxesLevies != null) this.taxesLeviesValue = Number(event.detail.taxesLevies) || 0;
    if (event.detail?.otherCosts != null) this.otherCostsValue = Number(event.detail.otherCosts) || 0;
    this._renderTotal();
    this._renderBreakdown();
  }

  updateVolDeclared(event) {
    if (this.hasPanelIdValue && event.detail?.panelId && event.detail.panelId !== this.panelIdValue) {
      return;
    }

    if (event.detail?.volDeclared == null) return;

    const volDeclared = Number(event.detail.volDeclared) || 0;

    if (this.hasVolDeclaredTarget) {
      this.volDeclaredTarget.textContent = formatNumber(volDeclared);
    }

    this._renderVolBoughtValidation(volDeclared);
  }

  updateGapTarget(event) {
    this.gapTargetValue = this._normalizeGapTarget(event.detail?.value);
    this._renderTotal();
    this._renderBreakdown();
    this._renderGapClosureLabel();
    this._renderFacilityRows();
    this._renderCountryRows();
  }

  updateCurrencyDisplay(event) {
    const value = this._normalizeCurrencyDisplay(event.detail?.value);
    if (!value) return;

    this.currencyDisplayValue = value;
    this._renderFacilityRows();
  }

  syncFromPayload(event) {
    const payload = event.detail?.payload;
    if (!payload || !this.hasPanelIdValue) return;

    const panel = payload.panels?.[this.panelIdValue];
    if (panel) {
      this.baseGapValue = panel.total_gap_payment;
      if (panel.taxes_levies != null) this.taxesLeviesValue = Number(panel.taxes_levies) || 0;
      if (panel.other_costs != null) this.otherCostsValue = Number(panel.other_costs) || 0;
      this._renderTotal();
      this._renderBreakdown();
    }
  }

  showLoading() {
    showModelingLoading();
  }

  hideLoading() {
    hideModelingLoading();
  }

  restoreAfterError() {
    hideModelingLoading();
  }

  _renderTotal() {
    if (!this.hasTotalContributionTarget) return;

    const base = contributionForGap(
      this.baseGapValue,
      this.gapTargetValue,
      this.taxesLeviesValue,
      this.otherCostsValue,
    );

    if (this.includeAddOnValue) {
      this.totalContributionTarget.dataset.baseContribution = String(base);
      this.totalContributionTarget.textContent = (base + currentTopUpAmount()).toLocaleString();
      return;
    }

    this.totalContributionTarget.textContent = base.toLocaleString();
  }

  _renderVolBoughtValidation(volDeclared) {
    if (!this.hasVolBoughtCellTarget) return;

    const volBought = Number(this.volBoughtCellTarget.dataset.volBought) || 0;
    const status = volBoughtValidationStatus(volBought, volDeclared);
    const nextClasses = VOL_BOUGHT_CELL_CLASSES[status] || VOL_BOUGHT_CELL_CLASSES.neutral;

    this.volBoughtCellTarget.classList.remove(...ALL_VOL_BOUGHT_CELL_CLASSES);
    this.volBoughtCellTarget.classList.add(...nextClasses);
    this.volBoughtCellTarget.dataset.volBoughtValidationStatus = status;
  }

  _renderBreakdown() {
    const costs = (Number(this.taxesLeviesValue) || 0) + (Number(this.otherCostsValue) || 0);
    const gross = Math.round((Number(this.baseGapValue) || 0) * (this.gapTargetValue / 100));

    if (this.hasGrossContributionTarget) {
      this.grossContributionTarget.textContent = gross.toLocaleString();
    }
    if (this.hasAddedCostsTarget) {
      this.addedCostsTarget.textContent = costs.toLocaleString();
    }
    if (this.hasContributionBreakdownTarget) {
      this.contributionBreakdownTarget.classList.toggle("hidden", costs <= 0);
    }
  }

  _renderGapClosureLabel() {
    if (!this.hasGapClosureLabelTarget || !this.gapClosureTemplateValue) return;

    this.gapClosureLabelTarget.textContent = this.gapClosureTemplateValue.replace(
      "%{percent}",
      String(Math.round(this.gapTargetValue))
    );
  }

  _renderFacilityRows() {
    const showFacilityCurrency = this.currencyDisplayValue === "facility";

    this.contributionCellTargets.forEach((cell) => {
      const gap = this._gapFor(cell, showFacilityCurrency);
      const leverage = this._leverageFor(cell);
      const contribution = contributionForGap(gap, this.gapTargetValue, 0, 0, leverage);
      this._setAmount(cell, contribution);
      this._setCurrency(cell, this._currencyFor(cell, showFacilityCurrency));
    });

    this.meanCellTargets.forEach((cell) => {
      const gap = this._gapFor(cell, showFacilityCurrency);
      const workers = Number(cell.dataset.workers) || 0;
      const leverage = this._leverageFor(cell);
      const contribution = contributionForGap(gap, this.gapTargetValue, 0, 0, leverage);
      const mean = workers > 0 ? Math.round(contribution / workers) : 0;
      this._setAmount(cell, mean);
      const currencyNode = cell.querySelector("[data-role='currency']");
      const currency = this._currencyFor(cell, showFacilityCurrency);
      if (currencyNode && currency) currencyNode.textContent = ` ${currency}`;
    });

    this.gtuCellTargets.forEach((cell) => {
      const raw = showFacilityCurrency ? cell.dataset.gtuLocal : cell.dataset.gtuPayment;
      const amountNode = cell.querySelector("[data-role='amount']");
      if (amountNode) {
        if (raw === undefined || raw === "") {
          amountNode.textContent = "—";
        } else {
          const gtu = Number(raw);
          amountNode.textContent = Number.isNaN(gtu)
            ? "—"
            : gtu.toLocaleString(undefined, { minimumFractionDigits: GTU_PRECISION, maximumFractionDigits: GTU_PRECISION });
        }
      }
      this._setCurrency(cell, this._currencyFor(cell, showFacilityCurrency));
    });
  }

  _renderCountryRows() {
    this.countryContributionTargets.forEach((cell) => {
      const gap = Number(cell.dataset.gapPayment) || 0;
      const contribution = Math.round(gap * (this.gapTargetValue / 100));
      this._setAmount(cell, contribution);
    });
  }

  _gapFor(cell, showFacilityCurrency) {
    const raw = showFacilityCurrency ? cell.dataset.gapFacility : cell.dataset.gapPayment;
    return Number(raw) || 0;
  }

  _leverageFor(cell) {
    const raw = Number(cell.dataset.buyingLeverage);
    return Number.isFinite(raw) ? raw : 0;
  }

  _currencyFor(cell, showFacilityCurrency) {
    if (showFacilityCurrency) {
      return cell.dataset.currencyFacility;
    }

    return cell.dataset.currencyPayment || this.paymentCurrencyCodeValue;
  }

  _setAmount(cell, value) {
    const node = cell.querySelector("[data-role='amount']");
    if (node) node.textContent = value.toLocaleString();
  }

  _setCurrency(cell, value) {
    const node = cell.querySelector("[data-role='currency']");
    if (node && value) node.textContent = value;
  }

  _normalizeGapTarget(raw) {
    const value = parseFloat(raw);
    if (Number.isNaN(value)) return 0;

    return Math.min(100, Math.max(0, value));
  }

  _normalizeCurrencyDisplay(raw) {
    if (raw === "facility" || raw === "local") return "facility";
    if (raw === "payment") return "payment";

    return null;
  }
}
